import React from 'react';
import { Link } from 'react-router-dom';
import { services, barbers, galleryImages } from '../mockData';
import { Star, Phone, Calendar, Scissors, Award, Users, Clock, Crown, Zap, Check, Dices, Trophy } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Home = () => {
  const { currentUser } = useAuth();

  return (
    <div className="sauce-sity-page" data-testid="home-page">
      <div className="sauce-sity-content">
        {/* Hero Section */}
        <section className="ss-hero" data-testid="hero-section">
          <h1 className="ss-title-xl">
            Where Style Meets <span className="ss-highlight-purple">Precision</span>
          </h1>
          <p className="ss-hero-subtext">
            Premium barbershop experience in Cape Coral. Expert barbers, timeless styles, and a vibe you won't find anywhere else.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '16px' }}>
            {currentUser ? (
              <Link to="/booking">
                <button className="ss-btn ss-btn-primary ss-btn-lg" data-testid="hero-book-btn">
                  <Calendar size={20} />
                  Book Appointment
                </button>
              </Link>
            ) : (
              <Link to="/signup">
                <button className="ss-btn ss-btn-primary ss-btn-lg" data-testid="hero-signup-btn">
                  <Calendar size={20} />
                  Get Started
                </button>
              </Link>
            )}
            <a href="tel:2392018105">
              <button className="ss-btn ss-btn-secondary ss-btn-lg" data-testid="hero-call-btn">
                <Phone size={20} />
                Call Now
              </button>
            </a>
          </div>
        </section>

        {/* Stats Section */}
        <section className="ss-grid-4 ss-mb-48" data-testid="stats-section">
          <div className="ss-stat-box">
            <Scissors className="w-10 h-10 mx-auto ss-mb-12" style={{ color: 'var(--ss-green)' }} />
            <div className="ss-stat-number">15+</div>
            <div className="ss-stat-label">Years Experience</div>
          </div>
          <div className="ss-stat-box">
            <Users className="w-10 h-10 mx-auto ss-mb-12" style={{ color: 'var(--ss-green)' }} />
            <div className="ss-stat-number">5000+</div>
            <div className="ss-stat-label">Happy Clients</div>
          </div>
          <div className="ss-stat-box">
            <Award className="w-10 h-10 mx-auto ss-mb-12" style={{ color: 'var(--ss-green)' }} />
            <div className="ss-stat-number">3</div>
            <div className="ss-stat-label">Expert Barbers</div>
          </div>
          <div className="ss-stat-box">
            <Star className="w-10 h-10 mx-auto ss-mb-12" style={{ color: 'var(--ss-green)' }} />
            <div className="ss-stat-number">4.8</div>
            <div className="ss-stat-label">Average Rating</div>
          </div>
        </section>

        {/* Hours + Phone */}
        <section className="ss-card ss-card-purple ss-mb-48" data-testid="hours-section">
          <div className="ss-grid-2" style={{ alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                background: 'rgba(170,255,0,0.12)',
                padding: '14px',
                borderRadius: '999px',
                display: 'inline-flex'
              }}>
                <Clock size={28} style={{ color: 'var(--ss-green)' }} />
              </div>
              <div>
                <h3 className="ss-title-md">Operating Hours</h3>
                <p style={{ color: 'var(--ss-green)', fontWeight: 700, margin: 0 }}>Tuesday – Saturday</p>
                <p className="ss-text" style={{ margin: 0 }}>8:30 AM – 6:00 PM</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                background: 'rgba(161,0,255,0.14)',
                padding: '14px',
                borderRadius: '999px',
                display: 'inline-flex'
              }}>
                <Phone size={28} style={{ color: 'var(--ss-purple)' }} />
              </div>
              <div>
                <h3 className="ss-title-md">Call Us Today</h3>
                <a
                  href="tel:2392018105"
                  data-testid="phone-link"
                  style={{ color: 'var(--ss-green)', fontWeight: 700, textDecoration: 'none', fontSize: '1.125rem' }}
                >
                  (239) 201-8105
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services Preview */}
        <section className="ss-mb-48" data-testid="services-section">
          <div className="ss-section-header">
            <h2 className="ss-section-title">Our <span className="ss-highlight-green">Services</span></h2>
            <p className="ss-section-subtitle">Premium grooming tailored to your style</p>
          </div>
          <div className="ss-grid-3">
            {services.map(service => (
              <div key={service.id} className="ss-feature-card" data-testid={`service-card-${service.id}`}>
                <div className="ss-feature-icon"><Scissors size={40} style={{ color: 'var(--ss-green)', margin: '0 auto' }} /></div>
                <h3 className="ss-feature-title">{service.name}</h3>
                <p className="ss-feature-desc ss-mb-16">{service.description}</p>
                <div style={{ color: 'var(--ss-white-soft)', fontSize: '0.875rem' }}>
                  <Clock size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                  {service.duration} min
                </div>
              </div>
            ))}
          </div>
          <div className="ss-text-center ss-mt-24">
            <Link to="/services">
              <button className="ss-btn ss-btn-secondary" data-testid="view-services-btn">View All Services</button>
            </Link>
          </div>
        </section>

        <div className="ss-divider"></div>

        {/* Membership Plans */}
        <section className="ss-mb-48" data-testid="membership-section">
          <div className="ss-section-header">
            <h2 className="ss-section-title">
              Become a <span className="ss-highlight-purple">Member</span>
            </h2>
            <p className="ss-section-subtitle">
              Priority booking, exclusive perks, and rewards on every visit
            </p>
          </div>

          <div className="ss-grid-2" style={{ maxWidth: 1000, margin: '0 auto' }}>
            <div className="ss-card ss-card-purple" data-testid="plan-regular">
              <div className="ss-text-center">
                <div style={{
                  background: 'rgba(161,0,255,0.18)',
                  width: 80, height: 80,
                  borderRadius: '999px',
                  display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: 16
                }}>
                  <Zap size={40} style={{ color: 'var(--ss-purple)' }} />
                </div>
                <h3 className="ss-title-lg">Sauce Sity Regular</h3>
                <div style={{ margin: '8px 0 20px 0' }}>
                  <span style={{ fontSize: '2.4rem', fontWeight: 800, color: 'var(--ss-purple)' }}>$29.99</span>
                  <span style={{ color: 'var(--ss-white-soft)' }}> /month</span>
                </div>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
                {['Priority booking access', 'Book holiday appointments 2 months early', 'Birthday discount 10% off', 'Free beard trims and lineup between services'].map((t, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', color: 'var(--ss-white-soft)', padding: '8px 0' }}>
                    <Check size={18} style={{ color: 'var(--ss-purple)', marginRight: 10, flexShrink: 0 }} />
                    {t}
                  </li>
                ))}
              </ul>
              <Link to="/membership">
                <button className="ss-btn ss-btn-secondary ss-btn-full" data-testid="regular-learn-more">Learn More</button>
              </Link>
            </div>

            <div className="ss-card ss-card-outline-green" style={{ position: 'relative' }} data-testid="plan-gentleman">
              <div style={{
                position: 'absolute', top: 0, right: 0,
                background: 'var(--ss-green)',
                color: '#111',
                padding: '6px 14px',
                fontSize: '0.75rem',
                fontWeight: 800,
                borderBottomLeftRadius: 12,
                borderTopRightRadius: 'var(--ss-radius-lg)'
              }}>
                MOST POPULAR
              </div>
              <div className="ss-text-center">
                <div style={{
                  background: 'rgba(170,255,0,0.15)',
                  width: 80, height: 80,
                  borderRadius: '999px',
                  display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: 16
                }}>
                  <Crown size={40} style={{ color: 'var(--ss-green)' }} />
                </div>
                <h3 className="ss-title-lg">The Gentleman</h3>
                <div style={{ margin: '8px 0 20px 0' }}>
                  <span style={{ fontSize: '2.4rem', fontWeight: 800, color: 'var(--ss-green)' }}>$49.99</span>
                  <span style={{ color: 'var(--ss-white-soft)' }}> /month</span>
                </div>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
                {['Everything in Sauce Sity Regular', 'Birthday discount 15% off', 'Free steam facials', '10% off merch and products'].map((t, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', color: 'var(--ss-white-soft)', padding: '8px 0' }}>
                    <Check size={18} style={{ color: 'var(--ss-green)', marginRight: 10, flexShrink: 0 }} />
                    {t}
                  </li>
                ))}
              </ul>
              <Link to="/membership">
                <button className="ss-btn ss-btn-primary ss-btn-full" data-testid="gentleman-learn-more">Learn More</button>
              </Link>
            </div>
          </div>
        </section>

        {/* Meet our barbers */}
        <section className="ss-mb-48" data-testid="barbers-section">
          <div className="ss-section-header">
            <h2 className="ss-section-title">Meet Our <span className="ss-highlight-green">Masters</span></h2>
            <p className="ss-section-subtitle">Expert barbers with a passion for perfection</p>
          </div>
          <div className="ss-grid-3">
            {barbers.map(barber => (
              <div key={barber.id} className="ss-card" style={{ padding: 0, overflow: 'hidden' }} data-testid={`barber-card-${barber.id}`}>
                {barber.avatar ? (
                  <img src={barber.avatar} alt={barber.name} style={{ width: '100%', height: 320, objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: 320, background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ color: 'var(--ss-white-soft)', fontSize: '0.875rem' }}>Add barber photo</p>
                  </div>
                )}
                <div style={{ padding: 20 }}>
                  <h3 className="ss-title-md">{barber.name}</h3>
                  <p style={{ color: 'var(--ss-purple)', fontSize: '0.875rem', margin: '4px 0 12px 0' }}>{barber.specialty}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'var(--ss-white-soft)', fontSize: '0.875rem' }}>
                    <span>{barber.experience}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Star size={16} style={{ color: 'var(--ss-green)', fill: 'var(--ss-green)' }} />
                      <span>{barber.rating}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pick A Number Game — preview card that routes to the real /pick-number-game page */}
        <section className="ss-mb-48 ss-mt-48" data-testid="home-pick-number-preview">
          <Link
            to="/pick-number-game"
            data-testid="home-pick-number-cta"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div
              className="ss-card ss-card-outline-green"
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                alignItems: 'center',
                gap: 24,
                padding: 28,
                cursor: 'pointer',
                background: 'radial-gradient(120% 80% at 0% 0%, rgba(170,255,0,0.10), rgba(161,0,255,0.10) 60%, rgba(0,0,0,0) 100%)',
              }}
            >
              <div style={{
                width: 72, height: 72, borderRadius: 20,
                background: 'rgba(170,255,0,0.15)',
                border: '1.5px solid rgba(170,255,0,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Dices size={36} style={{ color: 'var(--ss-green)' }} />
              </div>
              <div>
                <h2 className="ss-title-lg" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="ss-highlight-green">Pick a Number</span> Game
                  <Trophy size={20} style={{ color: 'gold' }} />
                </h2>
                <p className="ss-text" style={{ margin: '4px 0 0', opacity: 0.85 }}>
                  Weekly contest — pick your lucky number 1-100. Closest number to the draw wins the prize.
                </p>
              </div>
              <button className="ss-btn ss-btn-primary" style={{ whiteSpace: 'nowrap' }} data-testid="home-play-now-btn">
                Play now →
              </button>
            </div>
          </Link>
        </section>

        {/* Gallery Preview */}
        <section className="ss-mb-48 ss-mt-48" data-testid="gallery-section">
          <div className="ss-section-header">
            <h2 className="ss-section-title">Our <span className="ss-highlight-purple">Work</span></h2>
            <p className="ss-section-subtitle">Fresh cuts, clean fades, precision styles</p>
          </div>
          <div className="ss-grid-3">
            {galleryImages.slice(0, 6).map(img => (
              <div key={img.id} style={{
                position: 'relative', overflow: 'hidden', borderRadius: 'var(--ss-radius-lg)',
                border: '1px solid var(--ss-border)'
              }}>
                {img.url ? (
                  <img src={img.url} alt={img.alt} style={{ width: '100%', height: 240, objectFit: 'cover', display: 'block' }} />
                ) : (
                  <div style={{ width: '100%', height: 240, background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ color: 'var(--ss-white-soft)', fontSize: '0.875rem' }}>{img.alt}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="ss-text-center ss-mt-24">
            <Link to="/gallery">
              <button className="ss-btn ss-btn-secondary" data-testid="view-gallery-btn">View Full Gallery</button>
            </Link>
          </div>
        </section>

        {/* Final CTA */}
        <section className="ss-cta-section" data-testid="final-cta-section">
          <h2 className="ss-title-lg">
            Ready for Your <span className="ss-highlight-green">Fresh Look?</span>
          </h2>
          <p className="ss-text ss-text-lg ss-mb-24" style={{ maxWidth: 600, margin: '0 auto 24px auto' }}>
            Book your appointment today and experience the Sauce Sity difference
          </p>
          {currentUser ? (
            <Link to="/booking">
              <button className="ss-btn ss-btn-primary ss-btn-lg" data-testid="final-cta-book">
                Book Your Appointment Now
              </button>
            </Link>
          ) : (
            <Link to="/signup">
              <button className="ss-btn ss-btn-primary ss-btn-lg" data-testid="final-cta-signup">
                Get Started Today
              </button>
            </Link>
          )}
        </section>
      </div>
    </div>
  );
};

export default Home;
